import { makeAutoObservable, observable, runInAction } from 'mobx';
import type { TCompany } from '../../services/models/BillingModels';
import { resolveErrorMessage } from '../../utils/resolveErrorMessage';
import { groupOrganisationNumber, normaliseOrganisationNumber } from '../purchase/organisationNumber';

export interface ICompanyFieldStoreDependencies {
  /** Asks the company register for the companies a number or a name matches: `GET Companies?q=` (`billingApiAgent.Companies.lookup`). */
  lookup: (query: string) => Promise<TCompany[]>;
  /** The clock the pause in typing is measured on: resolves once `milliseconds` have passed. Driven by the tests. */
  wait: (milliseconds: number) => Promise<void>;
}

/** The backend's own wording for the same check, so the author reads one sentence whichever side caught it. */
export const INVALID_ORGANISATION_NUMBER_MESSAGE = 'The organisation number is not valid. It is nine digits, the last a check digit.';

export const UNKNOWN_ORGANISATION_NUMBER_MESSAGE = 'No company with that organisation number is registered in Brønnøysundregistrene.';

/** Our words for a lookup that failed without the backend's; the backend says the same when the register cannot be reached. */
export const COMPANY_REGISTER_UNREACHABLE_MESSAGE = 'The company register could not be reached. Please try again shortly.';

/** How long the author must stop typing a name before it is searched for. */
export const COMPANY_SEARCH_PAUSE_MS = 300;

/** How long a name must be, trimmed, before it is searched for. */
export const MINIMUM_COMPANY_NAME_LENGTH = 2;

/** How a suggestion reads in the company field's list: the register's name and the grouped number. */
export const suggestionLabel = (company: TCompany): string => `${company.name} · ${groupOrganisationNumber(company.orgNumber)}`;

/** How the company field came to stand for a registered company: its number typed, or its name picked from the suggestions. */
type TResolvedBy = 'number' | 'name';

/** What the company field resolved to, and how — which decides the caption. */
interface ICompanyResolution {
  company: TCompany;
  resolvedBy: TResolvedBy;
}

/**
 * The company field: the one field that names a company by organisation number or by name, and the
 * registered company it resolves to. Nine digits with a valid check digit are looked up in
 * Brønnøysundregistrene at once; a name — at least one letter, two characters or more — is searched for
 * after a pause in typing and offered as suggestions. Only the answer to what is in the field now
 * lands. A form uses the resolved company — the register's number and name — never what was typed.
 *
 * A form may prefill it with a company it already knows, resolved without a lookup, and lock it there:
 * the field then holds that company and ignores typing and picking.
 *
 * Imports no agent, no store and no React; the register lookup and the clock are ports. The purchase
 * store holds one; `CompanyField` renders one.
 */
export default class CompanyFieldStore {
  /** What is in the field: a number or a name, as typed — or a picked suggestion's name. */
  query = '';

  /** The registered companies the name in the field matched, newest answer only. */
  suggestions: TCompany[] = [];

  /** Why the field resolves to nothing — a bad check digit, an unknown number, an unreachable register — or null. */
  error: string | null = null;

  /** True while a lookup for the current field is on its way. */
  isLookingUp = false;

  /** True when the field is locked to a prefilled company: shown, not editable. */
  isLocked = false;

  private deps: ICompanyFieldStoreDependencies;

  /** Raised by every change of the field. A lookup's answer lands only if the field is as it was asked for. */
  private generation = 0;

  /** The registered company the field stands for and how it got there, or null until the field resolves. */
  private resolution: ICompanyResolution | null = null;

  constructor(dependencies: ICompanyFieldStoreDependencies) {
    this.deps = dependencies;

    makeAutoObservable<this, 'deps' | 'generation' | 'resolution'>(this, {
      deps: false,
      generation: false,
      resolution: observable.ref,
      suggestions: observable.ref,
    });
  }

  /** The registered company the field stands for, or null until it resolves to one. */
  get resolvedCompany(): TCompany | null {
    return this.resolution?.company ?? null;
  }

  /**
   * The small line above the field: the resolved company's other half, said for what it is — `Registered
   * as Acme AS` when the number was typed, `Org. no. 923 609 016` when the name was picked — so the
   * author knows the line is the register's answer, not a heading. Empty while nothing is resolved.
   */
  get caption(): string {
    if (this.resolution === null) return '';
    const { company, resolvedBy } = this.resolution;
    return resolvedBy === 'name' ? `Org. no. ${groupOrganisationNumber(company.orgNumber)}` : `Registered as ${company.name}`;
  }

  /**
   * What was typed in the field. Anything typed forgets the resolved company and the error. Nine digits
   * are checked and looked up at once; fewer show nothing yet. A name — at least one letter, two
   * characters or more — is searched for once typing pauses; other text is not searched. Ignored while
   * the field is locked.
   */
  setQuery = (value: string): void => {
    if (this.isLocked) return;
    this.query = value;
    this.clear();

    const compact = value.replace(/\s/g, '');
    if (/^\d+$/.test(compact)) {
      if (compact.length < 9) return;
      const orgNumber = normaliseOrganisationNumber(compact);
      if (orgNumber === null) {
        this.error = INVALID_ORGANISATION_NUMBER_MESSAGE;
        return;
      }
      void this.resolveNumber(orgNumber, this.generation);
      return;
    }

    const name = value.trim();
    if (!/\p{L}/u.test(name) || name.length < MINIMUM_COMPANY_NAME_LENGTH) return;
    void this.searchName(name, this.generation);
  };

  /**
   * A suggestion was picked: its name goes in the field, and the field resolves to it. Only a company
   * in the current list can be picked; anything else is ignored, as is any pick while the field is locked.
   */
  pickSuggestion = (picked: TCompany): void => {
    if (this.isLocked) return;
    const company = this.suggestions.find((suggestion) => suggestion.orgNumber === picked.orgNumber);
    if (company === undefined) return;
    this.clear();
    this.query = company.name;
    this.resolution = { company, resolvedBy: 'name' };
  };

  /**
   * Fills the field with a company the form already knows, as the register named it: resolved with no
   * lookup, the number in the field and the name in the caption. Locked, the number shows grouped, as
   * one the author reads rather than edits.
   */
  prefill = (company: TCompany, isLocked: boolean): void => {
    this.clear();
    this.isLocked = isLocked;
    this.query = isLocked ? groupOrganisationNumber(company.orgNumber) : company.orgNumber;
    this.resolution = { company, resolvedBy: 'number' };
  };

  /** Empties the field and unlocks it. A lookup on its way lands nowhere. */
  reset = (): void => {
    this.isLocked = false;
    this.query = '';
    this.clear();
  };

  /** Looks a valid number up at once; one company with it resolves the field, none is an error. */
  private resolveNumber = async (orgNumber: string, generation: number): Promise<void> => {
    this.isLookingUp = true;
    try {
      const companies = await this.deps.lookup(orgNumber);
      runInAction(() => {
        if (generation !== this.generation) return;
        this.isLookingUp = false;
        const company = companies.find((candidate) => candidate.orgNumber === orgNumber);
        if (company) {
          this.resolution = { company, resolvedBy: 'number' };
        } else {
          this.error = UNKNOWN_ORGANISATION_NUMBER_MESSAGE;
        }
      });
    } catch (error) {
      this.applyLookupFailure(error, generation);
    }
  };

  /** Searches a name once typing has paused, and offers what matched as suggestions. */
  private searchName = async (name: string, generation: number): Promise<void> => {
    await this.deps.wait(COMPANY_SEARCH_PAUSE_MS);
    if (generation !== this.generation) return;
    runInAction(() => {
      this.isLookingUp = true;
    });
    try {
      const companies = await this.deps.lookup(name);
      runInAction(() => {
        if (generation !== this.generation) return;
        this.isLookingUp = false;
        this.suggestions = companies;
      });
    } catch (error) {
      this.applyLookupFailure(error, generation);
    }
  };

  private applyLookupFailure(error: unknown, generation: number): void {
    runInAction(() => {
      if (generation !== this.generation) return;
      this.isLookingUp = false;
      this.suggestions = [];
      this.error = resolveErrorMessage(error, COMPANY_REGISTER_UNREACHABLE_MESSAGE);
    });
  }

  /**
   * Forgets everything the field had resolved, suggested or refused, and anything on its way: a lookup
   * asked for before this lands nowhere. Leaves what is in the field to the caller.
   */
  private clear(): void {
    this.generation += 1;
    this.resolution = null;
    // The list answered what was in the field before; it must not stay on screen or be picked while a new answer is on its way.
    this.suggestions = [];
    this.error = null;
    this.isLookingUp = false;
  }
}
